import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isNil } from '@semios/app-platform-common'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import type { GridTableContentSection, PossibleValuesRecord } from 'components/GridTable/types'
import { translate } from 'i18n/i18n'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'
import { sortByKey } from 'utils/sortByKey'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { defaultValuesRowHeight } from '../../_utils'
import { DropdownSelectorPropertyFlowMeter } from './DropdownSelectorPropertyFlowMeter'

const checkPermission = () =>
  selectedPropertyHasPermission({ permission: 'VIEW_IRRIGATION_FLOW_METER_DETAILS' })

export const flowMeterContent = ({
  data,
  points,
}: {
  data: routes.Values.Response
  points: TPoint[]
}): GridTableContentSection => {
  const title = translate.phrases.banyanApp('Flow Meter Daily Summary')

  const commonReturnItems = {
    title,
    fieldAssetPicker: <DropdownSelectorPropertyFlowMeter />,
    id: 'summary-grid-flowMeter',
  }

  if (!checkPermission() || points.length === 0) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const gridItems = points.map((point) => {
    const pointName = point.name
    const flowMeterData = data.points?.[point.lngLat]?.values?.irrigationFlowMeter?.[0]

    const shapedValues = (flowMeterData?.timeseries ?? []).reduce<Record<string, PossibleValuesRecord>>(
      (acc, curr) => {
        const { timestamp, ...rest } = curr

        acc[timestamp] = {
          ...rest,
          timestamp,
        }

        return acc
      },
      {},
    )

    return {
      id: point.lngLat,
      label: pointName,
      height: defaultValuesRowHeight,
      labelMinWidth: 90,
      valueMinWidth: 120,
      render: (dataPoint: {
        value: {
          flowMeterGallons: number | null
        }
      }) => {
        if (!dataPoint?.value) return translate.phrases.templates('-')

        const { flowMeterGallons } = dataPoint.value

        if (isNil(flowMeterGallons)) return translate.phrases.templates('-')

        return unitConverter.flowMeter(flowMeterGallons).valueWithSuffix()
      },
      values: shapedValues,
    }
  })

  return {
    title,
    fieldAssetPicker: <DropdownSelectorPropertyFlowMeter />,
    id: 'summary-grid-flowMeter',
    items: (gridItems || []).sort(sortByKey('label')),
  }
}
